import { useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { FindingStatus, useFailureAnalysis } from '../../models/indicationFailure'
import { FailureAnalysisDialog } from './FailureAnalysisDialog'

/** Why every carrier refused, rolled up by cause. Cai admins only. */
export function FailureAnalysisPanel({
  applicationId,
  isCaiAdmin,
}: {
  applicationId: string
  isCaiAdmin?: boolean
}) {
  const [open, setOpen] = useState(false)
  // The API serves the newest *finished* report, so a run in flight never blanks this.
  const { report, isAnalysing } = useFailureAnalysis(applicationId, !!isCaiAdmin)

  if (!isCaiAdmin || (!report && !isAnalysing)) return null

  const openFindings = report?.findings.filter((f) => f.status === FindingStatus.Open) ?? []
  if (!openFindings.length && !isAnalysing) return null

  const carriers = new Set(openFindings.flatMap((f) => f.blockedVendors))
  const fixable = openFindings.filter((f) => f.isFixable).length

  return (
    <>
      <Banner
        variant="warning"
        small
        align="center"
        // First child of the tab's flex column, so nothing above it supplies a gap.
        className="mt-4"
        action={
          <Button size="sm" variant="outline" disabled={!openFindings.length} onClick={() => setOpen(true)}>
            Review failures
          </Button>
        }
      >
        {isAnalysing
          ? 'Re-analysing why the carriers did not quote…'
          : `${carriers.size} carrier${carriers.size === 1 ? '' : 's'} failed · ${fixable} fixable`}
      </Banner>
      {open && report && (
        <FailureAnalysisDialog
          // Remount on a new report so its findings pick their pre-ticked values up again.
          key={report.id}
          applicationId={applicationId}
          report={report}
          isAnalysing={isAnalysing}
          open={open}
          onOpenChange={setOpen}
        />
      )}
    </>
  )
}
